import React, { useEffect, useRef, useState } from "react";
import "./css/setting.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { deleteSession } from "./common/session";

/* Daum 우편번호 스크립트 로더 */
function loadDaumPostcode() {
    return new Promise((resolve, reject) => {
        if (window.daum?.Postcode) return resolve();
        const s = document.createElement("script");
        s.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Daum Postcode script load failed"));
        document.head.appendChild(s);
    });
}

/* 휴대폰/유선 전화번호 자동 하이픈 포맷 */
function formatPhoneKOR(input = "") {
    const v = String(input).replace(/\D/g, "");
    if (!v) return "";
    if (v.startsWith("02")) {
        if (v.length <= 2) return v;
        if (v.length <= 5) return `02-${v.slice(2)}`;
        if (v.length <= 9) return `02-${v.slice(2, 5)}-${v.slice(5)}`;
        return `02-${v.slice(2, 6)}-${v.slice(6, 10)}`;
    }
    const head3 = v.slice(0, 3);
    if (v.length <= 3) return head3;
    if (v.length <= 7) return `${head3}-${v.slice(3)}`;
    if (v.length <= 10) return `${head3}-${v.slice(3, 6)}-${v.slice(6, 10)}`;
    return `${head3}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
}

export default function Setting() {
    const navigate = useNavigate();

    /* 기본 프로필 - 서버/localStorage에서 가져온 데이터 */
    const [profile, setProfile] = useState({
        name: "",
        postcode: "",
        baseAddr: "",
        extraAddr: "",
        detailAddr: "",
        email: "",
        phone: "", // 내부 저장은 숫자만
    });

    // 사용자 정보 로드
    useEffect(() => {
        const loadUserInfo = async () => {
            const API_BASE_URL =
                process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

            // localStorage에서 이메일 가져오기
            const email = localStorage.getItem("hb_serverEmail") ||
                localStorage.getItem("hb_googleEmail") ||
                "";

            if (!email) {
                // Fallback to localStorage
                const savedName =
                    localStorage.getItem("hb_serverName") ||
                    localStorage.getItem("hb_googleName") ||
                    "";
                const savedEmail = localStorage.getItem("hb_serverEmail") ||
                    localStorage.getItem("hb_googleEmail") ||
                    "";
                const savedPhone = localStorage.getItem("hb_phone") || "";
                const savedPostcode = localStorage.getItem("hb_postcode") || "";
                const savedBaseAddr = localStorage.getItem("hb_baseAddr") || "";
                const savedExtraAddr = localStorage.getItem("hb_extraAddr") || "";
                const savedDetailAddr = localStorage.getItem("hb_detailAddr") || "";

                setProfile({
                    name: savedName,
                    postcode: savedPostcode,
                    baseAddr: savedBaseAddr,
                    extraAddr: savedExtraAddr,
                    detailAddr: savedDetailAddr,
                    email: savedEmail,
                    phone: savedPhone,
                });
                return;
            }

            // 이메일로 사용자 정보 조회
            try {
                const res = await fetch(`${API_BASE_URL}/auth/user-by-email?email=${encodeURIComponent(email)}`, {
                    method: "GET",
                });

                if (res.ok) {
                    const data = await res.json();
                    const user = data.user || {};

                    // 서버에서 받은 정보로 프로필 설정
                    setProfile((prev) => ({
                        ...prev,
                        name: user.name || prev.name || "",
                        email: user.email || prev.email || "",
                        phone: user.phone || prev.phone || "",
                        // 주소는 localStorage에서 가져오거나 기본값 유지
                        postcode: prev.postcode || localStorage.getItem("hb_postcode") || "",
                        baseAddr: prev.baseAddr || localStorage.getItem("hb_baseAddr") || "",
                        extraAddr: prev.extraAddr || localStorage.getItem("hb_extraAddr") || "",
                        detailAddr: prev.detailAddr || localStorage.getItem("hb_detailAddr") || "",
                    }));
                } else {
                    // 서버 호출 실패 시 localStorage 사용
                    const savedName =
                        localStorage.getItem("hb_serverName") ||
                        localStorage.getItem("hb_googleName") ||
                        "";
                    const savedEmail = localStorage.getItem("hb_serverEmail") ||
                        localStorage.getItem("hb_googleEmail") ||
                        "";
                    const savedPhone = localStorage.getItem("hb_phone") || "";
                    const savedPostcode = localStorage.getItem("hb_postcode") || "";
                    const savedBaseAddr = localStorage.getItem("hb_baseAddr") || "";
                    const savedExtraAddr = localStorage.getItem("hb_extraAddr") || "";
                    const savedDetailAddr = localStorage.getItem("hb_detailAddr") || "";

                    setProfile({
                        name: savedName,
                        postcode: savedPostcode,
                        baseAddr: savedBaseAddr,
                        extraAddr: savedExtraAddr,
                        detailAddr: savedDetailAddr,
                        email: savedEmail,
                        phone: savedPhone,
                    });
                }
            } catch (e) {
                console.error("Failed to load user info from server", e);
                // 에러 시 localStorage 사용
                const savedName =
                    localStorage.getItem("hb_serverName") ||
                    localStorage.getItem("hb_googleName") ||
                    "";
                const savedEmail = localStorage.getItem("hb_serverEmail") ||
                    localStorage.getItem("hb_googleEmail") ||
                    "";
                const savedPhone = localStorage.getItem("hb_phone") || "";
                const savedPostcode = localStorage.getItem("hb_postcode") || "";
                const savedBaseAddr = localStorage.getItem("hb_baseAddr") || "";
                const savedExtraAddr = localStorage.getItem("hb_extraAddr") || "";
                const savedDetailAddr = localStorage.getItem("hb_detailAddr") || "";

                setProfile({
                    name: savedName,
                    postcode: savedPostcode,
                    baseAddr: savedBaseAddr,
                    extraAddr: savedExtraAddr,
                    detailAddr: savedDetailAddr,
                    email: savedEmail,
                    phone: savedPhone,
                });
            }
        };

        loadUserInfo();
    }, []);

    const displayAddress = `(${profile.postcode}) ${[profile.baseAddr, profile.extraAddr, profile.detailAddr]
        .filter(Boolean)
        .join(" ")}`;
    const displayPhone = formatPhoneKOR(profile.phone);

    /* 프로필 수정 모달 */
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({
        postcode: "",
        baseAddr: "",
        extraAddr: "",
        detailAddr: "",
        email: "",
        phone: "",
    });

    const fillDraftFromProfile = () =>
        setDraft({
            postcode: profile.postcode || "",
            baseAddr: profile.baseAddr || "",
            extraAddr: profile.extraAddr || "",
            detailAddr: profile.detailAddr || "",
            // 이름, 이메일, 전화번호는 draft에 포함하지 않음 (읽기 전용)
        });

    const onOpen = () => {
        fillDraftFromProfile();
        setOpen(true);
    };

    const onClose = () => setOpen(false);

    // ⬇️ 취소: 모달 닫기(복구 동작 제거)
    const onCancel = () => {
        setOpen(false);
    };

    const onSave = () => {
        // 주소만 저장 (이름, 이메일, 전화번호는 서버 데이터 사용)
        setProfile((prev) => ({
            ...prev,
            postcode: draft.postcode,
            baseAddr: draft.baseAddr,
            extraAddr: draft.extraAddr,
            detailAddr: draft.detailAddr,
        }));
        
        // 주소를 localStorage에 저장
        localStorage.setItem("hb_postcode", draft.postcode || "");
        localStorage.setItem("hb_baseAddr", draft.baseAddr || "");
        localStorage.setItem("hb_extraAddr", draft.extraAddr || "");
        localStorage.setItem("hb_detailAddr", draft.detailAddr || "");
        
        setOpen(false);
    };

    /* ESC 처리 */
    const [addrOpen, setAddrOpen] = useState(false);
    const [soonOpen, setSoonOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (addrOpen) setAddrOpen(false);
            else if (open) setOpen(false);
            else if (soonOpen) setSoonOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [addrOpen, open, soonOpen]);

    /* 주소 검색 레이어 */
    const modalBodyRef = useRef(null);
    const addrLayerRef = useRef(null);
    const detailInputRef = useRef(null);

    const openAddressSearch = async () => {
        try {
            await loadDaumPostcode();
            setAddrOpen(true);
            setTimeout(() => {
                const bodyEl = modalBodyRef.current;
                const wrapEl = addrLayerRef.current;
                if (!bodyEl || !wrapEl) return;
                const applyHeight = () => {
                    wrapEl.style.height = `${bodyEl.clientHeight}px`;
                };
                applyHeight();
                wrapEl.innerHTML = "";
                new window.daum.Postcode({
                    oncomplete: (data) => {
                        const roadAddr = data.roadAddress || "";
                        const jibunAddr = data.jibunAddress || "";
                        let extra = "";
                        if (data.bname && /[동|로|가]$/g.test(data.bname)) extra += data.bname;
                        if (data.buildingName && data.apartment === "Y") {
                            extra += (extra ? ", " : "") + data.buildingName;
                        }
                        if (extra) extra = `(${extra})`;
                        setDraft((p) => ({
                            ...p,
                            postcode: data.zonecode || "",
                            baseAddr: roadAddr || jibunAddr,
                            extraAddr: extra,
                        }));
                        setAddrOpen(false);
                        setTimeout(() => detailInputRef.current?.focus(), 0);
                    },
                    onresize: () => applyHeight(),
                    width: "100%",
                    height: "100%",
                    maxSuggestItems: 5,
                }).embed(wrapEl);
            }, 0);
        } catch (e) {
            console.error(e);
            alert("주소 검색 스크립트 로딩에 실패했습니다.");
        }
    };

    /* 메뉴 */
    const menuItems = [
        { label: "자격증 관리" },
        { label: "가입 관리" },
        { label: "생체인증 활성화" },
        // { label: "공지사항" },
        // { label: "자주 묻는 질문" },
        { label: "글자 크기 선택" },
        // { label: "개인 정보 약관 동의 보기" },
        // { label: "앱 버전" },
        { label: "비밀번호 변경" },
        { label: "로그아웃" },
    ];

    // 로그아웃 모달 상태
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joinList, setJoinList] = useState([]);
    const [selectedJoin, setSelectedJoin] = useState(null);

    const loadJoinEntries = () => {
        const entries = [];

        // 탐정법인홍익 모바일 신분증 또는 일반 자격증 중 하나라도 가입되어 있으면
        // "탐정법인홍익" 가입 정보 하나만 표시
        const mobileJoinRaw = localStorage.getItem("hb_mobile_join_info");
        const generalJoinRaw = localStorage.getItem("hb_general_join_info");
        
        let joinInfo = null;
        let joinedAt = null;
        
        // 탐정법인홍익 모바일 신분증 가입 정보 우선
        if (mobileJoinRaw) {
            try {
                joinInfo = JSON.parse(mobileJoinRaw);
                joinedAt = joinInfo.joinedAt;
            } catch (e) {
                console.error("Failed to parse hb_mobile_join_info", e);
            }
        }
        
        // 일반 자격증 가입 정보 (탐정법인홍익 모바일 신분증이 없을 때만 사용)
        if (!joinInfo && generalJoinRaw) {
            try {
                joinInfo = JSON.parse(generalJoinRaw);
                joinedAt = joinInfo.joinedAt;
            } catch (e) {
                console.error("Failed to parse hb_general_join_info", e);
            }
        }
        
        // 가입 정보가 있으면 "탐정법인홍익" 가입 정보 하나만 표시
        // 중복 방지를 위해 id로 확인
        if (joinInfo) {
            const existingIndex = entries.findIndex(e => e.id === "methodist-church");
            if (existingIndex === -1) {
                entries.push({
                    id: "methodist-church",
                    name: "탐정법인홍익",
                    org: "탐정법인홍익",
                    joinedAt: joinedAt,
                    church: joinInfo.church || "-", // 소속
                    type: "methodist",
                    fields: [
                        { label: "닉네임", value: joinInfo.nickname || "-" },
                        { label: "전화번호", value: joinInfo.phone || "-" },
                        { label: "소속", value: joinInfo.church || "-" },
                        { label: "이미지", value: joinInfo.image ? "있음" : "없음" },
                    ],
                });
            }
        }

        // 중복 제거: id가 같은 항목은 하나만 유지
        const uniqueEntries = [];
        const seenIds = new Set();
        entries.forEach(entry => {
            if (!seenIds.has(entry.id)) {
                seenIds.add(entry.id);
                uniqueEntries.push(entry);
            }
        });

        return uniqueEntries;
    };

    const openJoinModal = () => {
        const entries = loadJoinEntries();
        setJoinList(entries);
        setSelectedJoin(entries[0] || null);
        setShowJoinModal(true);
    };

    const closeJoinModal = () => setShowJoinModal(false);

    const handleJoinEdit = () => {
        if (!selectedJoin) return;
        
        // 탐정법인홍익 가입 정보 수정 -> 탐정법인홍익 모바일 신분증 가입수정 모드로 이동
        // (탐정법인홍익 모바일 신분증이 있으면 그것으로, 없으면 일반 자격증으로)
        const mobileJoinRaw = localStorage.getItem("hb_mobile_join_info");
        let cert, mode;
        
        if (mobileJoinRaw) {
            cert = { name: "탐정법인홍익 모바일 신분증", org: "탐정법인홍익" };
            mode = "join-edit";
        } else {
            const generalJoinRaw = localStorage.getItem("hb_general_join_info");
            if (generalJoinRaw) {
                try {
                    const info = JSON.parse(generalJoinRaw);
                    cert = { name: info.name || "탐정법인홍익 모바일 신분증", org: "탐정법인홍익" };
                    mode = "join";
                } catch (e) {
                    cert = { name: "탐정법인홍익 모바일 신분증", org: "탐정법인홍익" };
                    mode = "join";
                }
            } else {
                cert = { name: "탐정법인홍익 모바일 신분증", org: "탐정법인홍익" };
                mode = "join";
            }
        }
        
        navigate("/issuance", { state: { cert, mode, redirectTo: "/setting" } });
        setShowJoinModal(false);
    };

    const handleJoinLeave = () => {
        if (!selectedJoin) return;
        if (!window.confirm("가입 정보를 삭제하시겠습니까?")) return;

        // 탐정법인홍익 가입 정보 삭제 시 모든 가입 정보 제거
        localStorage.removeItem("hb_mobile_join_info");
        localStorage.removeItem("hb_mobile_id_joined");
        localStorage.removeItem("hb_pending_cert");
        localStorage.removeItem("hb_general_join_info");
        localStorage.removeItem("hb_general_joined");

        const refreshed = loadJoinEntries();
        setJoinList(refreshed);
        setSelectedJoin(refreshed[0] || null);
        if (refreshed.length === 0) {
            setShowJoinModal(false);
        }
    };

    const onClickMenu = (item) => {
        if (item.label === "로그아웃") {
            setShowLogoutModal(true);
        } else if (item.label === "자격증 관리") {
            navigate("/certificate-manage");
        } else if (item.label === "가입 관리") {
            openJoinModal();
        } else {
            setSoonOpen(true);
        }
    };

    // 로그아웃 확인 후 실제 로그아웃 실행
    const handleLogoutConfirm = async () => {
        setShowLogoutModal(false);
        
        // 1) 세션 삭제 (MySQL에서 삭제 및 모든 스토리지 삭제)
        // deleteSession() 내부에서 clearAllStorage()와 RN 메시지 전송이 모두 처리됨
        await deleteSession();

        // 2) 안드로이드 네이티브 WebView 인 경우 → 네이티브로 신호 전송 후 반환
        // (deleteSession() 내부에서 이미 전송되지만, 확실하게 한 번 더 전송)
        if (window.ReactNativeWebView) {
            try {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({ 
                        type: "LOGOUT",
                        clearAll: true 
                    })
                );
                console.log('📱 RN에 로그아웃 메시지 전송 완료');
            } catch (e) {
                console.error("RN postMessage 오류:", e);
            }
            return;
        }

        // 3) 일반 웹 브라우저인 경우 → 로그인 화면으로 이동
        navigate("/login", { replace: true });
    };

    // 로그아웃 모달 취소
    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    return (
        <div className="container-wrap setting-wrap">
            <div className="setting-viewport">
                {/* 헤더 로고 */}
                <header className="setting-header">
                    <img
                        src="/img/hongik_logo.png"
                        alt="탐정법인홍익"
                        className="setting-logo"
                        onClick={() => navigate("/")}
                    />
                </header>

                {/* 본문 */}
                <main className="setting-main">
                    {/* 프로필 카드 */}
                    <section
                        className="setting-card"
                        onClick={onOpen}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? onOpen() : null)}
                    >
                        {/* <div className="setting-avatar" aria-hidden>
                            <FontAwesomeIcon icon={faUser} style={{ fontSize: "28px", color: "#979797" }} />
                        </div> */}
                        <div className="setting-info">
                            <div className="setting-row">
                                <span className="setting-key">이름</span>
                                <span className="setting-value">{profile.name || "-"}</span>
                            </div>
                            <div className="setting-row">
                                <span className="setting-key">이메일</span>
                                <span className="setting-value">{profile.email || "-"}</span>
                            </div>
                            {/* <div className="setting-row">
                                <span className="setting-key">휴대폰</span>
                                <span className="setting-value">{displayPhone || "-"}</span>
                            </div>
                            <div className="setting-row">
                                <span className="setting-key">주소</span>
                                <span
                                    className="setting-value"
                                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4 }}
                                >
                                    {profile.postcode || profile.baseAddr || profile.detailAddr
                                        ? `(${profile.postcode || ""}) ${profile.baseAddr || ""}${profile.extraAddr ? " " + profile.extraAddr : ""}${profile.detailAddr ? " " + profile.detailAddr : ""}`
                                        : "-"}
                                </span>
                            </div> */}
                        </div>
                        <span className="setting-chevron">
                            <FontAwesomeIcon icon={faChevronRight} style={{ color: "gray" }} />
                        </span>
                    </section>

                    {/* 리스트 섹션 */}
                    <section className="setting-list">
                        {menuItems.map((item, idx) => (
                            <button
                                key={item.label + idx}
                                className="setting-item"
                                type="button"
                                onClick={() => onClickMenu(item)}
                            >
                                <span>{item.label}</span>
                                <span className="setting-chevron">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </span>
                            </button>
                        ))}
                    </section>
                </main>

                <div style={{ height: 100 }} />
            </div>

            {/* ===== 프로필 수정 모달 ===== */}
            {open && (
                <div className="setting-modal-overlay" onClick={onClose}>
                    <div className="setting-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="setting-modal-bar">
                            <b>프로필 수정</b>
                            {/* 상단 닫기 버튼 제거 */}
                        </div>

                        <div className="setting-modal-body">
                            {/* 이름 (읽기 전용) */}
                            <label className="setting-label">이름</label>
                            <input
                                className="setting-input"
                                type="text"
                                value={profile.name || ""}
                                readOnly
                                disabled
                                style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />

                            {/* 이메일 (읽기 전용) */}
                            <label className="setting-label">이메일</label>
                            <input
                                className="setting-input"
                                type="email"
                                value={profile.email || ""}
                                readOnly
                                disabled
                                style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />

                            {/* 휴대폰 (읽기 전용) */}
                            <label className="setting-label">휴대폰</label>
                            <input
                                className="setting-input"
                                type="tel"
                                value={displayPhone || ""}
                                readOnly
                                disabled
                                style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />

                            {/* 주소 블록 (수정 가능) */}
                            <div className="setting-fieldset">
                                <label className="setting-label">우편번호</label>
                                <div className="setting-input-row">
                                    <input
                                        className="setting-input"
                                        value={draft.postcode}
                                        onChange={(e) => setDraft((p) => ({ ...p, postcode: e.target.value }))}
                                        placeholder="우편번호"
                                    />
                                    <button type="button" className="setting-btn small" onClick={openAddressSearch}>
                                        검색
                                    </button>
                                </div>

                                <label className="setting-label">주소</label>
                                <input
                                    className="setting-input"
                                    value={draft.baseAddr}
                                    onChange={(e) => setDraft((p) => ({ ...p, baseAddr: e.target.value }))}
                                    placeholder="기본주소"
                                />

                                <label className="setting-label">상세주소</label>
                                <input
                                    className="setting-input"
                                    ref={detailInputRef}
                                    value={draft.detailAddr}
                                    onChange={(e) => setDraft((p) => ({ ...p, detailAddr: e.target.value }))}
                                    placeholder="상세주소"
                                />
                            </div>
                        </div>

                        <div className="setting-modal-actions">
                            <button className="setting-btn outline" onClick={onCancel}>취소</button>
                            <button className="setting-btn" onClick={onSave}>저장</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 주소 검색 레이어 ===== */}
            {addrOpen && (
                <div className="setting-modal-overlay" onClick={() => setAddrOpen(false)}>
                    <div className="setting-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="setting-modal-bar">
                            <b>주소 검색</b>
                            <button className="setting-close" onClick={() => setAddrOpen(false)}>닫기</button>
                        </div>
                        <div className="setting-modal-body setting-addr-body" ref={modalBodyRef}>
                            <div ref={addrLayerRef} style={{ width: "100%", height: "100%", minHeight: 420 }} />
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 가입 관리 모달 ===== */}
            {showJoinModal && (
                <div className="setting-modal-overlay" onClick={closeJoinModal}>
                    <div
                        className="setting-modal"
                        style={{ maxWidth: 560 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="setting-modal-bar">
                            <b>가입 관리</b>
                            <button className="setting-close" onClick={closeJoinModal}>닫기</button>
                        </div>
                        <div
                            className="setting-modal-body"
                            style={{
                                display: "grid",
                                gap: "12px",
                                maxHeight: "70vh",
                                overflowY: "auto",
                                padding: "16px 20px 24px",
                            }}
                        >
                            {joinList.length === 0 && (
                                <div style={{ textAlign: "center", color: "#666", padding: "30px 0" }}>
                                    가입/신청 내역이 없습니다.
                                </div>
                            )}

                            {joinList.map((item) => (
                                <button
                                    key={item.id}
                                    className="setting-item"
                                    style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "12px",
                                        textAlign: "left",
                                        background: selectedJoin?.id === item.id ? "#f7fafc" : "#fff",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "6px",
                                    }}
                                    onClick={() => setSelectedJoin(item)}
                                >
                                    <div style={{ fontSize: "16px", fontWeight: 700 }}>{item.name}</div>
                                    {/* {item.org && (
                                        <div style={{ fontSize: "13px", color: "#666" }}>{item.org}</div>
                                    )}
                                    {item.church && (
                                        <div style={{ fontSize: "13px", color: "#444" }}>
                                            교회: {item.church}
                                        </div>
                                    )} */}
                                    <div style={{ fontSize: "12px", color: "#999" }}>
                                        {item.joinedAt
                                            ? new Date(item.joinedAt).toLocaleString()
                                            : "기록 없음"}
                                    </div>
                                </button>
                            ))}

                            {selectedJoin && (
                                <div
                                    style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "12px",
                                        padding: "14px 16px",
                                        background: "#fff",
                                    }}
                                >
                                    <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: 8 }}>
                                        입력 항목
                                    </div>
                                    <div style={{ display: "grid", gap: "8px" }}>
                                        {selectedJoin.fields && selectedJoin.fields.length > 0 ? (
                                            selectedJoin.fields.map((f, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        fontSize: "14px",
                                                        color: "#111",
                                                    }}
                                                >
                                                    <span style={{ color: "#6b7280" }}>{f.label}</span>
                                                    <span>{f.value || "-"}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ fontSize: "14px", color: "#666" }}>
                                                입력 기록이 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="setting-modal-footer" style={{ padding: "12px 20px", display: "grid", gap: "8px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                                <button
                                    className="setting-btn outline"
                                    onClick={handleJoinEdit}
                                    disabled={!selectedJoin}
                                    style={{ opacity: selectedJoin ? 1 : 0.6 }}
                                >
                                    정보 수정
                                </button>
                                <button
                                    className="setting-btn"
                                    onClick={handleJoinLeave}
                                    disabled={!selectedJoin}
                                    style={{
                                        background: "#ef4444",
                                        color: "#fff",
                                        opacity: selectedJoin ? 1 : 0.6,
                                    }}
                                >
                                    탈퇴
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 커밍순 모달 ===== */}
            {soonOpen && (
                <div className="main-modal" role="dialog" aria-modal="true" aria-labelledby="soon-title" onClick={() => setSoonOpen(false)}>
                    <div className="main-modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="main-modal-body">
                            <h3 id="soon-title" className="main-modal-title">준비 중입니다.</h3>
                            <p className="main-modal-desc">해당 메뉴는 곧 제공될 예정이에요.</p>
                        </div>
                        <div className="main-modal-actions">
                            <button className="main-modal-btn" onClick={() => setSoonOpen(false)}>확인</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 로그아웃 확인 모달 ===== */}
            {showLogoutModal && (
                <div 
                    className="setting-modal-overlay" 
                    onClick={handleLogoutCancel}
                >
                    <div 
                        className="setting-modal" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "400px", width: "90%" }}
                    >
                        <div className="setting-modal-bar">
                            <b>로그아웃</b>
                        </div>
                        <div className="setting-modal-body" style={{ padding: "24px" }}>
                            <div style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#111",
                                marginBottom: "12px",
                                textAlign: "center"
                            }}>
                                로그아웃 하시겠습니까?
                            </div>
                            <div style={{
                                fontSize: "14px",
                                color: "#666",
                                textAlign: "center",
                                lineHeight: "1.5"
                            }}>
                                로그아웃 시 모든 세션 정보가 삭제됩니다.
                            </div>
                        </div>
                        <div className="setting-modal-actions" style={{ 
                            display: "flex", 
                            gap: "10px", 
                            padding: "16px 20px",
                            borderTop: "1px solid #eee"
                        }}>
                            <button
                                className="setting-btn outline"
                                onClick={handleLogoutCancel}
                                style={{ flex: 1 }}
                            >
                                취소
                            </button>
                            <button
                                className="setting-btn"
                                onClick={handleLogoutConfirm}
                                style={{ flex: 1, background: "#ff4d4f", color: "#fff" }}
                            >
                                로그아웃
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
